import { useState, useEffect, useRef, useCallback } from "react";
import tw from "tailwind-styled-components";
import { useMutation, useQuery } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import {
    BoxContainer,
    BoxTitle,
    BoxHead,
    Underline,
    FilterTableWrapper,
    FilterWrapperWithSearch,
} from "../../Components/Styles/PageStyles";
import NotFoundModel from "../../Components/NotFoundModel";
import { useSelector } from "react-redux";
import moment from "moment";
import DateRange from "../../Components/DateRange";
import SearchFilterBox from "../../Components/SearchFilterBox";
import SideModal from "../../Components/SideModal";
import PropertiesFilterDropdown from "../../Components/PropertiesFilterDropdown";
import { IoFilter } from "react-icons/io5";
import FilterModalCommission from "../../Components/StaffCommission/FilterModalCommission";
import Table from "../../Components/StaffCommission/StaffCommissionTable";
import { toast } from "react-toastify";
import DeleteCommissionModal from "../../Components/StaffCommission/DeleteCommissionModal";
import MarkAsPaidStaffCommission from "../../Components/StaffCommission/MarkAsPaidStaffCommission";
import EditCommission from "../../Components/StaffCommission/EditCommission";
import EditExpenses from "../../Components/Expenses/EditExpenses";

const StaffCommission = () => {
    const searchOptions = [
        { label: "Mobile", value: 1 },
        { label: "Name", value: 2 },
    ];

    const user = useSelector((state) => state.UserReducer.user);
    const properties = useSelector((state) => state.PropertyReducer.property);
    const property = useSelector(
        (state) => state.SelectPropertyReducer.selectProperty
    );

    const [activeTab, setActiveTab] = useState("1");
    const [selectedData, setSelectedData] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [staffFilters, setStaffFilters] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const [apiData, setApiData] = useState();
    const [staffList, setStaffList] = useState([]);
    const [isDeleteCommissionModal, setIsDeleteCommissionModal] = useState(false);
    const [isEditCommissionModal, setIsEditCommissionModal] = useState(false);
    const [isPayCommissionModal, setIsPayCommissionModal] = useState(false);
    const [expenseData, setExpenseData] = useState([]);
    const [isEditExpense, setIsEditExpense] = useState(false);

    const [startDate, setStartDate] = useState(
        moment().startOf("month").format("YYYY-MM-DD")
    );
    const [endDate, setEndDate] = useState(
        moment(new Date()).format("YYYY-MM-DD")
    );
    const [selectedProperty, setSelectedProperty] = useState(property);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const [activeFilter, setActiveFilter] = useState({
        value: "",
        label: "",
        title: "",
    });
    const hasMounted = useRef(false);
    const initialLoadDone = useRef(false);

    const parseFilterValue = (filterValue) => {
        if (!filterValue) return null;

        try {
            return JSON.parse(filterValue);
        } catch (error) {
            return filterValue;
        }
    };

    const formatFilterForDisplay = useCallback((parsedFilter) => {
        if (!parsedFilter?.selectedFilters) return null;

        const { selectedFilters } = parsedFilter;
        const filterCount =
            (selectedFilters.collectedBy?.length || 0);

        return {
            value: JSON.stringify(selectedFilters),
            label: `Filter (${filterCount})`,
            title: `Multi-Filter: ${filterCount} applied`
        };
    }, []);

    const buildQueryParams = useCallback(() => {
        const params = new URLSearchParams();
        params.append('pageNum', '1');
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        if (selectedProperty?.id) params.append('propFilters', selectedProperty.id);
        if (searchQuery) {
            params.append('s', searchQuery);
        }
        if (staffFilters) {
            params.append('staffFilters', staffFilters);
        }
        params.append('isPaid', activeTab === "2" ? "1" : "0");

        return params.toString();
    }, [startDate, endDate, activeTab, searchQuery, staffFilters, selectedProperty]);

    const fetchFunction = useCallback(async () => {
        const query = buildQueryParams();
        const response = await axios.get(
            `${Config.apiUrl}/staff-commission/list?${query}`,
            {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    }, [buildQueryParams, user.token]);

    const getSuccess = (res) => {
        if (res.isSuccess === false) {
            // Handle error
        } else {
            setApiData(res?.data);
            setStaffList(res?.staffs || []);
            setIsFocused(true);
        }
    };

    const getError = (res) => {
        //console.error("Error fetching transactions:", res);
    };

    const {
        isLoading,
        mutate: getListMutate,
        error,
        data,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    useEffect(() => {
        if (hasMounted.current) return;
        hasMounted.current = true;

        const timer = setTimeout(() => {
            if (!initialLoadDone.current) {
                getListMutate();
                initialLoadDone.current = true;
            }
        }, 50);

        return () => clearTimeout(timer);
    }, [getListMutate]);


    useEffect(() => {
        if (!hasMounted.current || !initialLoadDone.current) return;

        const timer = setTimeout(() => {
            getListMutate();
        }, 300);

        return () => clearTimeout(timer);
    }, [staffFilters, selectedProperty, startDate, endDate, searchQuery, activeTab, getListMutate]);

    const submitHandler = (values) => {
        const newValues = {
            startDate: moment(values.startDate).format("YYYY-MM-DD"),
            endDate: moment(values.endDate).format("YYYY-MM-DD"),
        };
        setSearchQuery("");
        setStartDate(newValues.startDate);
        setEndDate(newValues.endDate);
    };

    const handleFilterChange = (values) => {
        setSearchQuery(values.searchQuery);
        setStaffFilters('');
        setActiveFilter({ value: "", label: "", title: "" });
    };

    const onApplyFilter = (selectedFilters) => {
        const staffFiltersValue = selectedFilters.collectedBy
            .map(f => f.value)
            .join(',') || '';
        setStaffFilters(staffFiltersValue);

        const formattedFilter = formatFilterForDisplay({
            selectedFilters: {
                ...selectedFilters,
            }
        });

        setActiveFilter(formattedFilter || {
            value: JSON.stringify(selectedFilters),
            label: "Custom Filter",
            title: "Multi-Filter"
        });

        setIsFilterOpen(false);
        setSearchQuery("");
    };

    const handleClearFilters = () => {
        setActiveFilter({ value: "", label: "", title: "" });
        setStaffFilters("");
        setSearchQuery("");
        setIsFilterOpen(false);
    };

    const panelRef = useRef(null);
    const btnRef = useRef(null);

    useEffect(() => {
        if (!isFilterOpen) return;
        const onKey = (e) => {
            if (e.key === "Escape") setIsFilterOpen(false);
        };
        const onClickOutside = (e) => {
            if (
                panelRef.current &&
                !panelRef.current.contains(e.target) &&
                btnRef.current &&
                !btnRef.current.contains(e.target)
            ) {
                setIsFilterOpen(false);
            }
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onClickOutside);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onClickOutside);
        };
    }, [isFilterOpen]);

    const handlePropertySelect = (property) => {
        setSelectedProperty(property);
    };

    const handleFilterClose = () => {
        setIsFilterOpen(false);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setStartDate(startDate);
        setEndDate(endDate);
        setStaffFilters("");
        setActiveFilter({ value: "", label: "", title: "" });
    };

    //------- Commission Delete Modal -------
    const {
        isLoading: deleteCommissionLoading,
        mutate: deleteCommissionMutate,
    } = useMutation(
        (payload) =>
            axios.post(`${Config.apiUrl}/staff-commission/delete`, payload, {
                headers: {
                    authorization: `Bearer ${user.token}`,
                },
            }),
        {
            onSettled: () => {
                setIsDeleteCommissionModal(false);
            },
            onSuccess: ({ data }) => {
                getListMutate();
                toast.success(data?.msg || 'Success');
            },
            onError: (error) => {
                toast.error(error?.response?.data?.msg || 'An error occurred');
            },
        }
    );

    //------- Commission Edit Modal -------
    const {
        isLoading: editCommissionLoading,
        mutate: editCommissionMutate,
    } = useMutation(
        (payload) =>
            axios.post(`${Config.apiUrl}/staff-commission/edit`, payload, {
                headers: {
                    authorization: `Bearer ${user.token}`,
                },
            }),
        {
            onSettled: () => {
                setIsEditCommissionModal(false);
            },
            onSuccess: ({ data }) => {
                getListMutate();
                toast.success(data?.msg || 'Success');
            },
            onError: (error) => {
                toast.error(error?.response?.data?.msg || 'An error occurred');
            },
        }
    );

    //------- Commission Pay Modal -------
    const {
        isLoading: payCommissionLoading,
        mutate: payCommissionMutate,
    } = useMutation(
        (payload) =>
            axios.post(`${Config.apiUrl}/staff-commission/pay`, payload, {
                headers: {
                    authorization: `Bearer ${user.token}`,
                },
            }),
        {
            onSettled: () => {
                setIsPayCommissionModal(false);
            },
            onSuccess: ({ data }) => {
                getListMutate();
                toast.success(data?.msg || 'Success');
            },
            onError: (error) => {
                toast.error(error?.response?.data?.msg || 'An error occurred');
            },
        }
    );

    const fetchListing = async () =>
        await axios.get(`${Config.apiUrl}/expense/initial-values`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
            },
        });

    const { isLoading: isExpensesLoading, error: expensesError, data: expensesData, refetch, isRefetching: isExpensesRefetching } = useQuery(
        ['expenseData',],
        fetchListing,
        {
            enabled: isEditExpense,
            onSuccess: (response) => {
                setExpenseData(response?.data?.data);
            },
            onError: (error) => {
                console.error("Error fetching expense data:", error);
            }
        }
    );

    const categories = expenseData?.expenseCategories?.map((section) => ({
        type: "group",
        name: section.name,
        items: section.items.map((item) => ({
            value: item.id + "",
            label: item.name,
        })),
    }));


    //------- Edit Expense -------
    const editExpenseFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/expense/edit`, values, {
            headers: {
                Authorization: `Bearer ${user.token}`,
                "Content-Type": "application/x-www-form-urlenced; charset=UTF-8",
            },
        });
    };

    const onEditExpenseSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg || "Success");
            getListMutate();
            setIsEditExpense(false);
        } else {
            toast.error(data?.data?.msg || "An Error Occured");
        }
    };

    const onEditExpenseError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occured");
    };

    const { isLoading: editExpenseLoading, mutate: editExpenseMutate } =
        useMutation(editExpenseFunction, {
            onSuccess: onEditExpenseSuccess,
            onError: onEditExpenseError,
        });

    const handleEditCommissionModal = () => {
        setIsEditCommissionModal(false);
    };

    const handlePayCommissionModal = () => {
        setIsPayCommissionModal(false);
    };

    const handleExpansesClose = () => {
        setIsEditExpense(false);
    };
    return (
        <>
            <BoxContainer>
                <BoxHead>
                    <BoxTitle className="!mb-0 whitespace-nowrap">
                        Staff Commission
                        <Underline />
                    </BoxTitle>
                </BoxHead>

                <FloorTabs>
                    <FloorTab
                        key="unPaid"
                        onClick={() => handleTabChange('1')}
                        $active={activeTab === "1"}
                    >
                        Unpaid Commission (₹{data?.unpaidTotal || 0})
                    </FloorTab>
                    <FloorTab
                        key="paid"
                        onClick={() => handleTabChange('2')}
                        $active={activeTab === "2"}
                    >
                        Paid Commission (₹{data?.paidTotal || 0})
                    </FloorTab>

                </FloorTabs>

                <SideModal
                    isOpen={isFilterOpen}
                    onClose={() => setIsFilterOpen(false)}
                >
                    <FilterModalCommission
                        setIsFilterOpen={setIsFilterOpen}
                        onApplyFilter={onApplyFilter}
                        selectedData={selectedData}
                        handleClose={handleFilterClose}
                        currentFilter={parseFilterValue(activeFilter.value)}
                        handleClearFilters={handleClearFilters}
                        staffList={staffList}
                    />
                </SideModal>

                <SideModal
                    isOpen={isEditCommissionModal}
                    onClose={() => setIsEditCommissionModal(false)}
                >
                    <EditCommission
                        setIsEditCommissionModal={setIsEditCommissionModal}
                        isEditCommissionModal={isEditCommissionModal}
                        handleClose={handleEditCommissionModal}
                        selectedData={selectedData}
                        editCommissionLoading={editCommissionLoading}
                        editCommissionMutate={editCommissionMutate}
                        staffList={staffList}
                    />
                </SideModal>

                <SideModal
                    isOpen={isPayCommissionModal}
                    onClose={() => setIsPayCommissionModal(false)}
                >
                    <MarkAsPaidStaffCommission
                        isPayCommissionModal={isPayCommissionModal}
                        setIsPayCommissionModal={setIsPayCommissionModal}
                        handleClose={handlePayCommissionModal}
                        payCommissionLoading={payCommissionLoading}
                        payCommissionMutate={payCommissionMutate}
                        selectedStaff={selectedData}

                    />
                </SideModal>

                {isDeleteCommissionModal && (
                    <DeleteCommissionModal
                        setIsDeleteCommissionModal={setIsDeleteCommissionModal}
                        deleteCommissionLoading={deleteCommissionLoading}
                        deleteCommissionMutate={deleteCommissionMutate}
                        selectedData={selectedData}
                    />
                )}

                <SideModal isOpen={isEditExpense} onClose={() => setIsEditExpense(false)}
                >
                    <EditExpenses
                        setIsEditExpense={setIsEditExpense}
                        isEditExpense={isEditExpense}
                        handleClose={handleExpansesClose}
                        editExpenseLoading={editExpenseLoading}
                        editExpenseMutate={editExpenseMutate}
                        selectedData={selectedData}
                        expenseData={expenseData}
                        categories={categories}
                        property={properties}
                    />
                </SideModal>



                <FilterTableWrapper>
                    <FilterWrapperWithSearch>
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                            <SearchFilterBox
                                options={searchOptions}
                                handleFilterChange={handleFilterChange}
                                handleClearFilters={handleClearFilters}
                            />
                            <DateRange
                                submitHandler={submitHandler}
                                defaultLabel={
                                    moment(startDate).format("DD MMM yyyy") +
                                    " - " +
                                    moment(endDate).format("DD MMM yyyy")
                                }
                            />
                        </div>
                        <div className="flex gap-2 justify-end items-center">
                            <PropertiesFilterDropdown
                                properties={properties}
                                selectedProperty={selectedProperty}
                                onPropertySelect={handlePropertySelect}
                                placeholder="Select Property"
                                className="min-w-[200px] w-full md:w-[220px]"
                            />
                            {/* <div className="relative">
                                <FilterBtn
                                    ref={btnRef}
                                    type="button"
                                    title={activeFilter.title || "Open Filter"}
                                    onClick={() => setIsFilterOpen((v) => !v)}
                                >
                                    <IoFilter size={14} color="#9CA3AF" />
                                    {activeFilter?.value && (
                                        <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-yellow-500 border border-white" />
                                    )}
                                </FilterBtn>
                            </div> */}
                        </div>
                    </FilterWrapperWithSearch>
                    {!error && !isLoading && apiData && (
                        <TableWrapper >
                            <Table
                                apiData={error ? [] : apiData?.length > 0 ? apiData : []}
                                setSelectedData={setSelectedData}
                                selectedData={selectedData}
                                setIsDeleteCommissionModal={setIsDeleteCommissionModal}
                                setIsEditCommissionModal={setIsEditCommissionModal}
                                setIsPayCommissionModal={setIsPayCommissionModal}
                                setIsEditExpense={setIsEditExpense}
                            />
                            {(error || apiData.length === 0) && !isLoading && <NotFoundModel />}
                        </TableWrapper>
                    )}
                </FilterTableWrapper>

                {error && !isLoading && <NotFoundModel />}
                {(isLoading || isRefetching) && <Loading />}
            </BoxContainer>
        </>
    );
};

const TableWrapper = tw.h1`mt-4 bg-white rounded-md`;
const FilterBtn = tw.button`flex items-center border whitespace-nowrap border-gray-300 justify-center rounded-md px-4 cursor-pointer bg-white hover:bg-gray-200 h-10 text-sm ml-auto`;
const FloorTabs = tw.div`flex overflow-x-auto bg-white rounded-md my-5 border-b`;
const FloorTab = tw.button`
  ${(p) =>
        p.$active
            ? " text-primary-color border-b-2 border-b-primary-color bg-cyan-50 "
            : " text-gray-700 "} 
   px-5 py-3  cursor-pointer font-medium transition-all duration-200 whitespace-nowrap text-sm
`;
export default StaffCommission;